import React, { useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Input,
  NumberInput,
  NumberInputField,
  Spinner,
  Stack,
  Text
} from '@chakra-ui/react'
import moment from 'moment'
import { rentalCredits } from '@homevest/utils'
import axios from 'lib/axios'
import { BooksLockedNotice, useBuildiumPostingGuard } from 'lib/buildiumBooksLock'
import BuildiumPostingSummary from '../BuildiumPostingSummary'
import { RentalDetailsContext } from '../RentalDetailsContext'
import {
  CreditLiabilityTypeSelect,
  CreditNoteField,
  NonRentCreditWarning
} from './CreditFormFields'

const REACT_APP_RENT_LIABILITY_TYPE_ID = process.env.REACT_APP_RENT_LIABILITY_TYPE_ID!
type CreatedRentalCredit = { id: string }

const IssueCreditForm: React.FC<
  React.PropsWithChildren<{
    onDirtyChange?: (isDirty: boolean) => void
    onSubmit: (createdCreditIds: string[]) => void | Promise<void>
    rentalId: string
  }>
> = ({ onDirtyChange, onSubmit, rentalId }) => {
  const startOfMonth = moment().startOf('month')
  const initialDate = startOfMonth.format('YYYY-MM-DD')
  const [price, setPrice] = useState('')
  const [note, setNote] = useState('')
  const [date, setDate] = useState(initialDate)
  const [isMoveInCredit, setIsMoveInCredit] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [liabilityTypeId, setLiabilityTypeId] = useState(REACT_APP_RENT_LIABILITY_TYPE_ID)
  const [hasConfirmedOverride, setHasConfirmedOverride] = useState(false)
  const { liabilityTypes } = useContext(RentalDetailsContext)
  const { booksLock, isBooksLockLoading, isBooksLockError, isBuildiumBooksLocked } =
    useBuildiumPostingGuard({
      date,
      rentalId
    })
  const selectedLiabilityType = liabilityTypes.find(
    (liabilityType) => liabilityType.id === liabilityTypeId
  )
  const numericPrice = Number(price)
  const isDirty =
    price !== '' ||
    note.trim().length > 0 ||
    date !== initialDate ||
    isMoveInCredit ||
    liabilityTypeId !== REACT_APP_RENT_LIABILITY_TYPE_ID ||
    hasConfirmedOverride

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  const getDateError = () => {
    if (!date) {
      return 'Date is required.'
    }

    if (!moment(date).isValid()) {
      return 'Enter a valid date.'
    }

    return null
  }

  const dateError = getDateError()
  const priceError =
    price !== '' && (!Number.isFinite(numericPrice) || numericPrice <= 0)
      ? 'Enter an amount greater than $0.00.'
      : null
  const needsLiabilityTypeOverride = liabilityTypeId !== REACT_APP_RENT_LIABILITY_TYPE_ID
  const isValid =
    !dateError &&
    !priceError &&
    Number.isFinite(numericPrice) &&
    numericPrice > 0 &&
    note.trim().length > 0 &&
    Boolean(liabilityTypeId) &&
    !isBooksLockLoading &&
    !isBooksLockError &&
    !isBuildiumBooksLocked &&
    (!needsLiabilityTypeOverride || hasConfirmedOverride)

  const handleSubmit = async () => {
    if (!isValid) {
      return
    }

    setIsSaving(true)
    setSaveError(null)

    try {
      const { data: createdCredit } = await axios.post<CreatedRentalCredit>(
        '/admin/rental_credits',
        {
          amount: numericPrice,
          date,
          note: note.trim(),
          rental_id: rentalId,
          type: rentalCredits.RENTAL_CREDIT_TYPES.EXCHANGE,
          liability_type_id: liabilityTypeId,
          move_in_credit: isMoveInCredit
        }
      )
      setPrice('')
      setDate('')
      setNote('')
      setLiabilityTypeId(REACT_APP_RENT_LIABILITY_TYPE_ID)
      await onSubmit([createdCredit.id])
    } catch (error: any) {
      setSaveError(error?.message || 'Unable to issue rental credit')
    } finally {
      setIsSaving(false)
    }
  }

  const toggleIsMoveInCredit = () => {
    if (isMoveInCredit) {
      setNote('')
      setIsMoveInCredit(false)
    } else {
      setLiabilityTypeId(REACT_APP_RENT_LIABILITY_TYPE_ID)
      setNote('Move-In Credit')
      setIsMoveInCredit(true)
    }
  }

  return (
    <React.Suspense
      fallback={
        <Center py={12}>
          <Spinner />
        </Center>
      }
    >
      <Stack spacing={5}>
        {saveError && (
          <Alert status='error'>
            <AlertIcon />
            {saveError}
          </Alert>
        )}

        <Checkbox isChecked={isMoveInCredit} onChange={toggleIsMoveInCredit} isDisabled={isSaving}>
          This is a move-in credit
        </Checkbox>

        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))' }} gap={4}>
          <FormControl isRequired isInvalid={Boolean(priceError)} isDisabled={isSaving}>
            <FormLabel>Amount</FormLabel>
            <NumberInput min={0.01} precision={2} value={price} onChange={setPrice}>
              <NumberInputField placeholder='0.00' />
            </NumberInput>
            <FormErrorMessage>{priceError}</FormErrorMessage>
          </FormControl>

          <FormControl
            isRequired
            isInvalid={Boolean(dateError) || isBooksLockError || isBuildiumBooksLocked}
            isDisabled={isSaving}
          >
            <FormLabel>Date to credit</FormLabel>
            <Input type='date' value={date} onChange={(event) => setDate(event.target.value)} />
            {dateError && <FormErrorMessage>{dateError}</FormErrorMessage>}
          </FormControl>

          <CreditLiabilityTypeSelect
            isDisabled={isSaving}
            liabilityTypeId={liabilityTypeId}
            liabilityTypes={liabilityTypes}
            onChange={(nextLiabilityTypeId) => {
              setLiabilityTypeId(nextLiabilityTypeId)
              setHasConfirmedOverride(false)
            }}
          />

          <BuildiumPostingSummary
            amount={numericPrice}
            date={date}
            dateError={dateError}
            entryDescription='One rental credit'
            isBooksLockError={isBooksLockError}
            isBooksLockLoading={isBooksLockLoading}
            isBuildiumBooksLocked={isBuildiumBooksLocked}
            lockDate={booksLock?.lockDate}
            typeLabel={selectedLiabilityType?.name}
          />
        </Grid>

        {isBooksLockLoading && <Text fontSize='sm'>Checking Buildium books-lock status...</Text>}
        {(isBooksLockError || isBuildiumBooksLocked) && (
          <BooksLockedNotice lockDate={booksLock?.lockDate} isError={isBooksLockError} />
        )}

        {needsLiabilityTypeOverride && (
          <NonRentCreditWarning
            example='E.g. if a tenant was accidentally charged for utilities and has not paid yet, credit Utilities or delete the original utilities charge. If they already paid, credit Rent.'
            isChecked={hasConfirmedOverride}
            isDisabled={isSaving}
            onChange={setHasConfirmedOverride}
          />
        )}

        <CreditNoteField isDisabled={isSaving} note={note} onChange={setNote} />

        <Box display='flex' justifyContent='flex-end'>
          <Button
            colorScheme='teal'
            onClick={handleSubmit}
            isDisabled={!isValid || isSaving}
            isLoading={isSaving}
          >
            Issue Rental Credit
          </Button>
        </Box>
      </Stack>
    </React.Suspense>
  )
}

IssueCreditForm.propTypes = {
  onDirtyChange: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  rentalId: PropTypes.string.isRequired
}

export default IssueCreditForm
